import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * Manages the inline form for adding custom steps to a placement plan.
 * Toggles between the "+ Add" button and the expanded form, then
 * POSTs the new step as a Turbo Stream request.
 *
 * Connects to data-controller="custom-step-form"
 */
export default class extends Controller {
  static targets = ["addButton", "form", "title", "actionTypeRadio"]
  static values = {
    url: String,
  }

  declare readonly addButtonTarget: HTMLElement
  declare readonly formTarget: HTMLElement
  declare readonly titleTarget: HTMLInputElement
  declare readonly actionTypeRadioTargets: HTMLInputElement[]
  declare readonly urlValue: string

  showForm() {
    this.addButtonTarget.classList.add("hidden")
    this.formTarget.classList.remove("hidden")
    this.titleTarget.focus()
  }

  hideForm() {
    this.formTarget.classList.add("hidden")
    this.addButtonTarget.classList.remove("hidden")
    this.clearInputs()
  }

  async submit() {
    const title = this.titleTarget.value.trim()
    if (!title) {
      this.titleTarget.focus()
      return
    }

    const csrfToken =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || ""

    const formData = new FormData()
    formData.append("custom_step[title]", title)

    const actionType = this.selectedActionType()
    if (actionType) {
      formData.append("custom_step[action_type]", actionType)
    }

    const requiredInput =
      this.formTarget.querySelector<HTMLInputElement>(
        "[name='custom_step[required]']",
      )
    if (requiredInput) {
      formData.append(
        "custom_step[required]",
        requiredInput.type === "checkbox"
          ? requiredInput.checked
            ? "1"
            : "0"
          : requiredInput.value,
      )
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
        body: formData,
        credentials: "same-origin",
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.hideForm()
      } else {
        console.error(
          `Failed to add custom step: ${response.status} ${response.statusText}`,
        )
      }
    } catch (error) {
      console.error("Error adding custom step:", error)
    }
  }

  private selectedActionType(): string | null {
    const checked = this.actionTypeRadioTargets.find((r) => r.checked)
    return checked?.value ?? null
  }

  private clearInputs() {
    this.titleTarget.value = ""

    const requiredInput =
      this.formTarget.querySelector<HTMLInputElement>(
        "[name='custom_step[required]']",
      )
    if (requiredInput && requiredInput.type === "checkbox") {
      requiredInput.checked = false
    }

    // Reset radio selection to the first option
    this.actionTypeRadioTargets.forEach((radio, idx) => {
      radio.checked = idx === 0
    })
  }
}
